import {
  validateCharacterGroveConfig,
  type CharacterGroveConfig,
  type CharacterGroveHint,
} from './CharacterGroveConfig.js';

export type CharacterGrovePhase =
  | 'read'
  | 'inspect-character'
  | 'choose-insight'
  | 'find-supporting-moment'
  | 'connect'
  | 'complete';

export type CharacterGroveConnectionResult =
  | 'incomplete'
  | 'insight-mismatch'
  | 'evidence-mismatch'
  | 'correct';

export interface CharacterGroveConnection {
  readonly result: CharacterGroveConnectionResult;
  readonly insightMatches: boolean;
  readonly evidenceMatches: boolean;
}

export interface CharacterGroveSnapshot {
  readonly challengeId: string;
  readonly phase: CharacterGrovePhase;
  readonly storyPageIndex: number;
  readonly inspectedCharacter: boolean;
  readonly selectedInsightId?: string;
  readonly selectedEvidenceIds: readonly string[];
  readonly hintStage: number;
  readonly attempts: number;
  readonly lastConnection?: CharacterGroveConnection;
  readonly completed: boolean;
}

const PHASES: readonly CharacterGrovePhase[] = [
  'read',
  'inspect-character',
  'choose-insight',
  'find-supporting-moment',
  'connect',
  'complete',
];

export function isCharacterGroveSnapshot(value: unknown): value is CharacterGroveSnapshot {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as Partial<CharacterGroveSnapshot>;
  return typeof snapshot.challengeId === 'string'
    && typeof snapshot.phase === 'string'
    && PHASES.includes(snapshot.phase as CharacterGrovePhase)
    && Number.isInteger(snapshot.storyPageIndex)
    && typeof snapshot.inspectedCharacter === 'boolean'
    && Array.isArray(snapshot.selectedEvidenceIds)
    && Number.isInteger(snapshot.hintStage)
    && Number.isInteger(snapshot.attempts)
    && typeof snapshot.completed === 'boolean';
}

/** Rendering-independent Memory Branches state, safe to preserve across relayout. */
export class CharacterGroveState {
  private phaseValue: CharacterGrovePhase = 'read';
  private storyPageIndexValue = 0;
  private inspectedCharacterValue = false;
  private selectedInsightIdValue?: string;
  private readonly selectedEvidenceIdsValue: string[] = [];
  private hintStageValue = 0;
  private attemptsValue = 0;
  private lastConnectionValue?: CharacterGroveConnection;
  private completedValue = false;

  constructor(
    readonly config: CharacterGroveConfig,
    initialSnapshot?: CharacterGroveSnapshot,
  ) {
    validateCharacterGroveConfig(config);
    if (initialSnapshot) this.restore(initialSnapshot);
  }

  get isComplete() { return this.completedValue; }

  snapshot(): CharacterGroveSnapshot {
    return {
      challengeId: this.config.id,
      phase: this.phaseValue,
      storyPageIndex: this.storyPageIndexValue,
      inspectedCharacter: this.inspectedCharacterValue,
      selectedInsightId: this.selectedInsightIdValue,
      selectedEvidenceIds: [...this.selectedEvidenceIdsValue],
      hintStage: this.hintStageValue,
      attempts: this.attemptsValue,
      lastConnection: this.lastConnectionValue,
      completed: this.completedValue,
    };
  }

  setStoryPage(index: number) {
    if (this.phaseValue !== 'read' || !Number.isInteger(index)
        || index < 0 || index >= this.config.story.pages.length) return false;
    this.storyPageIndexValue = index;
    return true;
  }

  nextStoryPage() {
    return this.setStoryPage(Math.min(
      this.storyPageIndexValue + 1,
      this.config.story.pages.length - 1,
    ));
  }

  previousStoryPage() {
    return this.setStoryPage(Math.max(0, this.storyPageIndexValue - 1));
  }

  finishReading() {
    if (this.phaseValue !== 'read'
        || this.storyPageIndexValue !== this.config.story.pages.length - 1) return false;
    this.phaseValue = 'inspect-character';
    return true;
  }

  inspectCharacter() {
    if (this.phaseValue !== 'inspect-character') return false;
    this.inspectedCharacterValue = true;
    this.phaseValue = 'choose-insight';
    return true;
  }

  selectInsight(insightId: string) {
    if (this.completedValue
        || !['choose-insight', 'find-supporting-moment', 'connect'].includes(this.phaseValue)
        || !this.config.insightChoices.some(choice => choice.id === insightId)) return false;
    this.selectedInsightIdValue = insightId;
    this.lastConnectionValue = undefined;
    this.phaseValue = this.hasEnoughEvidence ? 'connect' : 'find-supporting-moment';
    return true;
  }

  selectEvidence(evidenceId: string) {
    if (this.completedValue
        || !['find-supporting-moment', 'connect'].includes(this.phaseValue)
        || !this.config.evidenceMoments.some(moment => moment.id === evidenceId)) return false;

    const selectedIndex = this.selectedEvidenceIdsValue.indexOf(evidenceId);
    if (selectedIndex >= 0) {
      this.selectedEvidenceIdsValue.splice(selectedIndex, 1);
    } else {
      while (this.selectedEvidenceIdsValue.length >= this.config.requiredEvidenceCount) {
        this.selectedEvidenceIdsValue.shift();
      }
      this.selectedEvidenceIdsValue.push(evidenceId);
    }
    this.lastConnectionValue = undefined;
    this.phaseValue = this.hasEnoughEvidence ? 'connect' : 'find-supporting-moment';
    return true;
  }

  connect(): CharacterGroveConnectionResult {
    if (this.completedValue) return 'correct';
    if (!this.selectedInsightIdValue || !this.hasEnoughEvidence) {
      this.lastConnectionValue = {
        result: 'incomplete',
        insightMatches: this.selectedInsightIdValue === this.config.correctInsightId,
        evidenceMatches: false,
      };
      return 'incomplete';
    }

    this.attemptsValue += 1;
    const insightMatches = this.selectedInsightIdValue === this.config.correctInsightId;
    const evidenceMatches = this.selectedEvidenceIdsValue.every(id =>
      this.config.supportingEvidenceIds.includes(id));
    const result: CharacterGroveConnectionResult = insightMatches && evidenceMatches
      ? 'correct'
      : insightMatches ? 'evidence-mismatch' : 'insight-mismatch';
    this.lastConnectionValue = { result, insightMatches, evidenceMatches };
    if (result === 'correct') {
      this.completedValue = true;
      this.phaseValue = 'complete';
    }
    return result;
  }

  requestHint(): CharacterGroveHint {
    const hint = this.config.hints[Math.min(this.hintStageValue, this.config.hints.length - 1)];
    this.hintStageValue = Math.min(this.hintStageValue + 1, this.config.hints.length);
    return hint;
  }

  reset() {
    const changed = this.phaseValue !== 'read'
      || this.storyPageIndexValue !== 0
      || this.inspectedCharacterValue
      || this.selectedInsightIdValue !== undefined
      || this.selectedEvidenceIdsValue.length > 0
      || this.hintStageValue > 0
      || this.attemptsValue > 0
      || this.lastConnectionValue !== undefined;
    this.phaseValue = 'read';
    this.storyPageIndexValue = 0;
    this.inspectedCharacterValue = false;
    this.selectedInsightIdValue = undefined;
    this.selectedEvidenceIdsValue.length = 0;
    this.hintStageValue = 0;
    this.attemptsValue = 0;
    this.lastConnectionValue = undefined;
    this.completedValue = false;
    return changed;
  }

  private get hasEnoughEvidence() {
    return this.selectedEvidenceIdsValue.length === this.config.requiredEvidenceCount;
  }

  private restore(snapshot: CharacterGroveSnapshot) {
    if (!isCharacterGroveSnapshot(snapshot) || snapshot.challengeId !== this.config.id) return;
    this.storyPageIndexValue = Math.max(
      0,
      Math.min(snapshot.storyPageIndex, this.config.story.pages.length - 1),
    );
    this.inspectedCharacterValue = snapshot.inspectedCharacter;
    this.hintStageValue = Math.max(0, Math.min(snapshot.hintStage, this.config.hints.length));
    this.attemptsValue = Math.max(0, snapshot.attempts);

    if (snapshot.selectedInsightId
        && this.config.insightChoices.some(choice => choice.id === snapshot.selectedInsightId)) {
      this.selectedInsightIdValue = snapshot.selectedInsightId;
    }
    const knownEvidence = new Set(this.config.evidenceMoments.map(moment => moment.id));
    snapshot.selectedEvidenceIds.forEach(id => {
      if (knownEvidence.has(id)
          && !this.selectedEvidenceIdsValue.includes(id)
          && this.selectedEvidenceIdsValue.length < this.config.requiredEvidenceCount) {
        this.selectedEvidenceIdsValue.push(id);
      }
    });

    const pairIsCorrect = this.selectedInsightIdValue === this.config.correctInsightId
      && this.hasEnoughEvidence
      && this.selectedEvidenceIdsValue.every(id => this.config.supportingEvidenceIds.includes(id));
    if (snapshot.completed && snapshot.phase === 'complete' && pairIsCorrect) {
      this.completedValue = true;
      this.phaseValue = 'complete';
      this.lastConnectionValue = { result: 'correct', insightMatches: true, evidenceMatches: true };
      return;
    }

    if (snapshot.lastConnection?.result && snapshot.lastConnection.result !== 'correct') {
      this.lastConnectionValue = snapshot.lastConnection;
    }
    const requestedPhaseIsValid = PHASES.includes(snapshot.phase)
      && snapshot.phase !== 'complete'
      && (snapshot.phase === 'read'
        || snapshot.phase === 'inspect-character'
        || (snapshot.phase === 'choose-insight' && this.inspectedCharacterValue)
        || (snapshot.phase === 'find-supporting-moment' && !!this.selectedInsightIdValue)
        || (snapshot.phase === 'connect' && !!this.selectedInsightIdValue && this.hasEnoughEvidence));
    if (requestedPhaseIsValid) {
      this.phaseValue = snapshot.phase;
    } else if (this.selectedInsightIdValue && this.hasEnoughEvidence) {
      this.phaseValue = 'connect';
    } else if (this.selectedInsightIdValue) {
      this.phaseValue = 'find-supporting-moment';
    } else if (this.inspectedCharacterValue) {
      this.phaseValue = 'choose-insight';
    }
  }
}
